import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { X } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Button,
  Input,
  Card,
  CardHeader,
  CardBody,
  ConfirmDialog,
} from '../../../design-system/components';
import {
  admin_fraud_root_path,
  admin_fraud_rules_path,
  admin_fraud_disposable_emails_path,
  admin_fraud_disposable_email_path,
  sync_admin_fraud_disposable_emails_path,
} from '../../../lib/routes';
export default function DisposableEmails({ domains }) {
  const [newDomain, setNewDomain] = useState('');
  const [batchMode, setBatchMode] = useState(false);
  const [batchDomains, setBatchDomains] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingDomain, setRemovingDomain] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const filteredDomains = searchFilter
    ? domains.filter((d) => d.includes(searchFilter.toLowerCase()))
    : domains;
  function handleAddSingle(e) {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAdding(true);
    router.post(
      admin_fraud_disposable_emails_path(),
      {
        domain: newDomain.trim().toLowerCase(),
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setAdding(false);
          setNewDomain('');
        },
      },
    );
  }
  function handleAddBatch(e) {
    e.preventDefault();
    const domainList = batchDomains
      .split(/[\n,]+/)
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d.length > 0);
    if (domainList.length === 0) return;
    setAdding(true);
    // Add domains one at a time — sequential posts
    let index = 0;
    function addNext() {
      if (index >= domainList.length) {
        setAdding(false);
        setBatchDomains('');
        setBatchMode(false);
        return;
      }
      router.post(
        admin_fraud_disposable_emails_path(),
        {
          domain: domainList[index],
        },
        {
          preserveScroll: true,
          onFinish: () => {
            index++;
            addNext();
          },
        },
      );
    }
    addNext();
  }
  function handleSync() {
    setSyncing(true);
    router.post(sync_admin_fraud_disposable_emails_path(), {}, {
      preserveScroll: true,
      onFinish: () => setSyncing(false),
    });
  }
  function handleRemove() {
    if (!removingDomain) return;
    router.delete(
      `${admin_fraud_disposable_email_path('_')}?domain=${encodeURIComponent(removingDomain)}`,
      {
        preserveScroll: true,
        onFinish: () => setRemovingDomain(null),
      },
    );
  }
  return (
    <AdminLayout>
      <PageHeader
        title="Disposable Email Domains"
        description={`${domains.length} blocked domain${domains.length !== 1 ? 's' : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              loading={syncing}
            >
              Sync from disposables.app
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_rules_path())}
            >
              Rules
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_root_path())}
            >
              Back to Events
            </Button>
          </div>
        }
      />

      <div className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-fg">Add Domains</h3>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => setBatchMode(!batchMode)}
              >
                {batchMode ? 'Single mode' : 'Batch mode'}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {batchMode ? (
              <form onSubmit={handleAddBatch} className="space-y-3">
                <textarea
                  className="w-full rounded-md border border-edge px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                  rows={4}
                  placeholder="Paste multiple domains, one per line or comma-separated"
                  value={batchDomains}
                  onChange={(e) => setBatchDomains(e.target.value)}
                />
                <Button type="submit" size="sm" loading={adding}>
                  Add Domains
                </Button>
              </form>
            ) : (
              <form onSubmit={handleAddSingle} className="flex items-end gap-3">
                <div className="flex-1">
                  <Input
                    label="Domain"
                    placeholder="example.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                </div>
                <Button type="submit" size="sm" loading={adding}>
                  Add
                </Button>
              </form>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-fg">
                Blocked Domains ({filteredDomains.length})
              </h3>
              {domains.length > 10 && (
                <div className="w-64">
                  <Input
                    placeholder="Filter domains..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {filteredDomains.length === 0 ? (
              <p className="text-sm text-fg-tertiary">
                {searchFilter ? 'No domains match your filter.' : 'No domains blocked yet.'}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {filteredDomains.map((domain) => (
                  <div
                    key={domain}
                    className="flex items-center justify-between rounded-md border border-edge-strong px-3 py-2"
                  >
                    <span className="text-sm text-fg-secondary font-mono">{domain}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 !p-0.5 text-fg-tertiary hover:text-danger-500"
                      onClick={() => setRemovingDomain(domain)}
                      title="Remove domain"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={removingDomain !== null}
        onClose={() => setRemovingDomain(null)}
        onConfirm={handleRemove}
        title="Remove Domain"
        message={`Are you sure you want to remove "${removingDomain}" from the blocklist?`}
        confirmLabel="Remove"
        destructive
      />
    </AdminLayout>
  );
}
